import { enforceSessionPolicy } from "../shared/authUtils";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import authService from "../services/authService";
import { AuthContextType, User } from "../types/auth";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Função para inicializar estado do localStorage de forma síncrona
const initializeAuthState = () => {
  try {
    enforceSessionPolicy();
    const savedToken = localStorage.getItem("access_token");
    const savedUser = localStorage.getItem("user");

    if (savedToken && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        return {
          user: parsedUser,
          token: savedToken,
          isAuthenticated: true,
        };
      } catch (parseError) {
        console.warn("⚠️ [AuthContext] Erro ao fazer parse do usuário salvo");
        // Limpar dados inválidos
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("user");
      }
    }
  } catch (error) {
    console.error("💥 [AuthContext] Erro ao inicializar estado:", error);
  }

  return {
    user: null,
    token: null,
    isAuthenticated: false,
  };
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // ✅ Inicializar estado diretamente do localStorage de forma síncrona
  const initialState = initializeAuthState();
  const [user, setUser] = useState<User | null>(initialState.user);
  const [token, setToken] = useState<string | null>(initialState.token);
  const [isAuthenticated, setIsAuthenticated] = useState(
    initialState.isAuthenticated,
  );
  // ✅ Se já temos dados iniciais, não precisamos mostrar loading
  const [isLoading, setIsLoading] = useState(false);

  // Função auxiliar para limpar dados de autenticação
  const clearAuthData = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
  };

  // ✅ Função simplificada - apenas carregar do localStorage sem validação imediata
  const loadAuthFromStorage = () => {
    try {
      const savedToken = localStorage.getItem("access_token");
      const savedUser = localStorage.getItem("user");

      if (savedToken && savedUser) {
        try {
          const parsedUser = JSON.parse(savedUser);
          setUser(parsedUser);
          setToken(savedToken);
          setIsAuthenticated(true);
        } catch (parseError) {
          console.error("❌ [AuthContext] Erro ao fazer parse do usuário");
          clearAuthData();
        }
      } else {
        // Não há dados salvos
        setUser(null);
        setToken(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error(
        "💥 [AuthContext] Erro ao carregar auth do storage:",
        error,
      );
      clearAuthData();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAuthFromStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (
    email: string,
    password: string,
    remember = true,
  ): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await authService.login(email, password, remember);

      // ✅ Garantir que os dados estão salvos no localStorage
      // (authService.login já faz isso, mas vamos garantir)
      if (response.access_token && response.user) {
        localStorage.setItem("access_token", response.access_token);
        localStorage.setItem("user", JSON.stringify(response.user));

        // Atualizar estado
        setUser(response.user);
        setToken(response.access_token);
        setIsAuthenticated(true);
      } else {
        throw new Error(
          "Resposta de login inválida: faltam dados de autenticação",
        );
      }
    } catch (error) {
      // Limpar estado e localStorage em caso de erro
      setUser(null);
      setToken(null);
      setIsAuthenticated(false);
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (
    email: string,
    name: string,
    password: string,
    company?: string,
    phone?: string,
  ): Promise<void> => {
    setIsLoading(true);
    try {
      await authService.register(email, name, password, company, phone);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = (redirectToLogin: boolean = true): void => {
    authService.logout(redirectToLogin);
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");

    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
  };

  const refreshToken = async (): Promise<boolean> => {
    try {
      const success = await authService.renewToken();
      if (success) {
        const savedToken = localStorage.getItem("access_token");
        if (savedToken) {
          setToken(savedToken);
          // Garantir que o estado de autenticação permanece true
          if (!isAuthenticated && user) {
            setIsAuthenticated(true);
          }
          return true;
        }
        return false;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  // useCallback aqui não é só estilo — sem isso, `updateUser` é uma nova
  // função a cada render do AuthProvider. `ActiveContextContext.loadContextOptions`
  // tem `updateUser` na sua lista de dependências, então essa instabilidade
  // se propaga: loadContextOptions muda de identidade a cada render →
  // o useEffect que a chama dispara de novo → setState → novo render →
  // updateUser muda de novo → loop infinito de "/context-options" e de
  // tudo que depende do contexto ativo (leads, stats, etc.).
  const updateUser = useCallback((updatedUser: User): void => {
    // Atualizar localStorage
    localStorage.setItem("user", JSON.stringify(updatedUser));
    // Atualizar estado
    setUser(updatedUser);
  }, []);

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    isAuthenticated,
    login,
    register,
    logout,
    refreshToken,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
